import { useState, useEffect, useMemo, useRef, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { ColumnDef } from '../../../../components/DataTable';
import Pagination from '../../../../components/Pagination/Pagination';
import { CredCockpitContainer, TitleContainer } from './style';
import {
  FilterContainer,
  FilterGroup,
  FilterLabel,
  FilterSelect,
  ClearButton,
  LoadingContainer
} from './style';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSearch, faSpinner, faEye } from '@fortawesome/free-solid-svg-icons';
import '../../../../styles/analytical-table.css';
import { useApi } from '../../../../hooks/ApiGateway';
import { API_CONFIG } from '../../../../config/api';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import FiltersCard from '../../../../components/FiltersCard/FiltersCard';
import Button from '../../../../components/Button/Button';
import Select from '../../../../components/Select/Select';
import { PermissionGate } from '../../../../contexts/PermissionGate';
import { authService } from '../../../../services/authService';

interface DataType {
  id_remessa: string;
  fundo: string;
  cedente: string;
  originador: string;
  data_cessao: string;
  valor_aquisicao: string;
  valor_nominal: string;
  quantidade_titulos: number;
  aprovador: string[];
  status: string;
  id_status: number;
  nome_remessa: string;
  id_produto: number;
  disabled: boolean;
}

type CessaoRow = {
  id_remessa: string;
  nome_remessa: string;
  fundo: string;
  cedente: string;
  originador: string;
  data_cessao: string;
  valor_aquisicao: number;
  valor_nominal: number;
  quantidade_titulos: number;
  aprovador: string;
  status: string;
  id_produto: number;
  id_status: number;
  disabled: boolean;
};
  
interface FundoCedente {
    fundoId: number;
    cnpjFundo: string;
    nomeFundo: string;
    cedenteId: number;
    cnpjCedente: string;
    nomeCedente: string;
}

interface OriginadorCedente {
    originadorId: number;
    cnpjOriginador: string;
    nomeOriginador: string;
    cedenteId: number;
    cnpjCedente: string;
    nomeCedente: string;
}

interface FundoOriginador {
    fundoId: number;
    cnpjFundo: string;
    nomeFundo: string;
    originadorId: number;
    cnpjOriginador: string;
    nomeOriginador: string;
}

interface Relacionamentos {
    fundoCedentes: FundoCedente[];
    originadorCedentes: OriginadorCedente[];
    fundoOriginadores: FundoOriginador[];
}

interface OperacoesFiltersState {
  fundo: string;
  aprovador: string;
  originador: string;
  cedente: string;
  status: string;
  nome_remessa: string;
}

interface OperacoesPersistedState {
  filters: OperacoesFiltersState;
  appliedFilters: OperacoesFiltersState;
  dataCessaoFiltro: string;
  pendingDate: string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  currentPage: number;
  pageSize: number;
}

const OPERACOES_STORAGE_KEY = 'operacoes_filters_state';

const getTodayDate = (): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getEndOfCurrentMonth = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0);
};

const getEmptyFilters = (): OperacoesFiltersState => ({
  fundo: '',
  aprovador: '',
  originador: '',
  cedente: '',
  status: '',
  nome_remessa: ''
});

const loadPersistedState = (): OperacoesPersistedState | null => {
  try {
    const rawValue = sessionStorage.getItem(OPERACOES_STORAGE_KEY);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as Partial<OperacoesPersistedState>;
    if (!parsed || typeof parsed !== 'object') return null;

    const emptyFilters = getEmptyFilters();

    return {
      filters: {
        ...emptyFilters,
        ...(parsed.filters || {}),
      },
      appliedFilters: {
        ...emptyFilters,
        ...(parsed.appliedFilters || {}),
      },
      dataCessaoFiltro: typeof parsed.dataCessaoFiltro === 'string' ? parsed.dataCessaoFiltro : getTodayDate(),
      pendingDate: typeof parsed.pendingDate === 'string' ? parsed.pendingDate : getTodayDate(),
      sortField: typeof parsed.sortField === 'string' ? parsed.sortField : '',
      sortDirection: parsed.sortDirection === 'desc' ? 'desc' : 'asc',
      currentPage: typeof parsed.currentPage === 'number' ? parsed.currentPage : 0,
      pageSize: typeof parsed.pageSize === 'number' ? parsed.pageSize : 10,
    };
  } catch {
    return null;
  }
};

const intersectSets = (a: Set<string> | null, b: Set<string> | null): Set<string> | null => {
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  return new Set(Array.from(a).filter(x => b!.has(x)));
};

const getValidFundoNames = (rel: Relacionamentos, cedente: string, originador: string): Set<string> | null => {
  const byCedente = cedente ? new Set(rel.fundoCedentes.filter(fc => fc.nomeCedente === cedente).map(fc => fc.nomeFundo)) : null;
  const byOriginador = originador ? new Set(rel.fundoOriginadores.filter(fo => fo.nomeOriginador === originador).map(fo => fo.nomeFundo)) : null;
  return intersectSets(byCedente, byOriginador);
};

const getValidCedenteNames = (rel: Relacionamentos, fundo: string, originador: string): Set<string> | null => {
  const byFundo = fundo ? new Set(rel.fundoCedentes.filter(fc => fc.nomeFundo === fundo).map(fc => fc.nomeCedente)) : null;
  const byOriginador = originador ? new Set(rel.originadorCedentes.filter(oc => oc.nomeOriginador === originador).map(oc => oc.nomeCedente)) : null;
  return intersectSets(byFundo, byOriginador);
};

const getValidOriginadorNames = (rel: Relacionamentos, fundo: string, cedente: string): Set<string> | null => {
  const byFundo = fundo ? new Set(rel.fundoOriginadores.filter(fo => fo.nomeFundo === fundo).map(fo => fo.nomeOriginador)) : null;
  const byCedente = cedente ? new Set(rel.originadorCedentes.filter(oc => oc.nomeCedente === cedente).map(oc => oc.nomeOriginador)) : null;
  return intersectSets(byFundo, byCedente);
};

const STATUS_RANGES = [
  { max: 98, cls: 'status-yellow' },
  { max: 199, cls: 'status-green' },
  { max: 299, cls: 'status-yellow' },
] as const;

const getStatusClass = (id_status: number): string => {
  if (!Number.isFinite(id_status)) return 'status-red';
  for (const range of STATUS_RANGES) {
    if (id_status <= range.max) return range.cls;
  }
  return 'status-red';
};

let lastDateCache: string | null = null;
const CredCockpit = () => {
  const persistedState = loadPersistedState();
  const defaultFilters = getEmptyFilters();

  const [filters, setFilters] = useState<OperacoesFiltersState>(persistedState?.filters || defaultFilters);

  const [appliedFilters, setAppliedFilters] = useState<OperacoesFiltersState>(persistedState?.appliedFilters || defaultFilters);

  const [dataCessaoFiltro, setDataCessaoFiltro] = useState(() => {
    if (persistedState?.dataCessaoFiltro) return persistedState.dataCessaoFiltro;
    if (lastDateCache) return lastDateCache;
    return getTodayDate();
  });

  const [pendingDate, setPendingDate] = useState(persistedState?.pendingDate || dataCessaoFiltro);

  const formattedDateTime = dataCessaoFiltro.split('-').reverse().join('/');

  const endOfCurrentMonth = useMemo(() => getEndOfCurrentMonth(), []);

  const { makeGetRequest } = useApi<DataType>();
  const [linhaCessoes, setLinhaCessoes] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const requestSeq = useRef(0);

  const [sortField, setSortField] = useState(persistedState?.sortField || '');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(persistedState?.sortDirection || 'asc');
  const [currentPage, setCurrentPage] = useState(persistedState?.currentPage || 0);
  const [pageSize, setPageSize] = useState(persistedState?.pageSize || 10);

  useEffect(() => {
    sessionStorage.setItem(
      OPERACOES_STORAGE_KEY,
      JSON.stringify({
        filters,
        appliedFilters,
        dataCessaoFiltro,
        pendingDate,
        sortField,
        sortDirection,
        currentPage,
        pageSize,
      } satisfies OperacoesPersistedState)
    );
  }, [filters, appliedFilters, dataCessaoFiltro, pendingDate, sortField, sortDirection, currentPage, pageSize]);

  const handleSort = (field: string) => {
    setSortDirection(prev => sortField === field && prev === 'asc' ? 'desc' : 'asc');
    setSortField(field);
    setCurrentPage(0);
  };

  
const [relacionamentos, setRelacionamentos] = useState<Relacionamentos | null>(null);

  const fetchRelacionamentos = async () => {
              try {
                  const token = await authService.getValidToken();
                  const response = await fetch(`${API_CONFIG.BASE_URL_JAVA}/api/v1/empresas/relacionamentos`, {
                      headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`,
                      },
                  });
                  if (response.ok) {
                      const data: Relacionamentos = await response.json();
                      setRelacionamentos(data);
                  }
              } catch (err) {
                  console.error('Erro ao buscar relacionamentos:', err);
              }
          };
    


  const fetchData = async (dataOperacao: string) => {
    const mySeq = ++requestSeq.current;
    try {
      setLoading(true);
      const result = await makeGetRequest(
        `${API_CONFIG.BASE_URL}/cessao/detalhe?dataOperacao=${dataOperacao}`
      );
      const data = JSON.parse(result.body.body);

      if (mySeq !== requestSeq.current) return;

      setLinhaCessoes(data);
    } catch (error) {
      if (mySeq !== requestSeq.current) return;

      console.error('Erro ao buscar dados:', error);
      setLinhaCessoes([]);
    } finally {
      if (mySeq === requestSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(dataCessaoFiltro);
    lastDateCache = dataCessaoFiltro;
    fetchRelacionamentos();
    const interval = setInterval(() => {
      fetchData(dataCessaoFiltro);
    }, 1 * 60 * 5000);

    return () => clearInterval(interval);
  }, [dataCessaoFiltro]);

  const transformedData = useMemo(() => {
    return linhaCessoes.map(linha => {
      const idStatusNum = Number(linha.id_status);
      return {
        id_remessa: linha.id_remessa,
        nome_remessa: linha.nome_remessa,
        fundo: linha.fundo,
        cedente: linha.cedente,
        originador: linha.originador,
        data_cessao: linha.data_cessao,
        valor_aquisicao: parseFloat(linha.valor_aquisicao.replace(/[^\d,]/g, '').replace(',', '.')),
        valor_nominal: parseFloat(linha.valor_nominal.replace(/[^\d,]/g, '').replace(',', '.')),
        quantidade_titulos: linha.quantidade_titulos,
        aprovador: linha.aprovador.join(', '),
        status: linha.status,
        id_produto: linha.id_produto,
        id_status: idStatusNum,
        disabled: linha.disabled,
      };
    });
  }, [linhaCessoes]);

  const { fundoOptions, cedenteOptions, originadorOptions } = useMemo(() => {
    if (!relacionamentos) return { fundoOptions: [] as string[], cedenteOptions: [] as string[], originadorOptions: [] as string[] };
    const validFundos = getValidFundoNames(relacionamentos, filters.cedente, filters.originador);
    const validCedentes = getValidCedenteNames(relacionamentos, filters.fundo, filters.originador);
    const validOriginadores = getValidOriginadorNames(relacionamentos, filters.fundo, filters.cedente);

    const fundoSet = new Set<string>();
    relacionamentos.fundoOriginadores.forEach(fo => fundoSet.add(fo.nomeFundo));

    const cedenteSet = new Set<string>();
    relacionamentos.fundoCedentes.forEach(fc => cedenteSet.add(fc.nomeCedente));
    relacionamentos.originadorCedentes.forEach(oc => cedenteSet.add(oc.nomeCedente));

    const originadorSet = new Set<string>();
    relacionamentos.originadorCedentes.forEach(oc => originadorSet.add(oc.nomeOriginador));

    return {
      fundoOptions: Array.from(fundoSet).filter(n => !validFundos || validFundos.has(n)),
      cedenteOptions: Array.from(cedenteSet).filter(n => !validCedentes || validCedentes.has(n)),
      originadorOptions: Array.from(originadorSet).filter(n => !validOriginadores || validOriginadores.has(n)),
    };
  }, [relacionamentos, filters.fundo, filters.cedente, filters.originador]);

  const uniqueAprovadores = useMemo(() => {
    const allAprovadores = linhaCessoes.flatMap(linha => linha.aprovador);
    return Array.from(new Set(allAprovadores));
  }, [linhaCessoes]);
  const uniqueStatus = useMemo(() => Array.from(new Set(linhaCessoes.map(linha => linha.status))), [linhaCessoes]);

  const filteredLinhaCessoes = useMemo(() => {
    return transformedData.filter(linha => {
      const matchesFundo = !appliedFilters.fundo || linha.fundo === appliedFilters.fundo;
      const matchesAprovador = !appliedFilters.aprovador || linha.aprovador.includes(appliedFilters.aprovador);
      const matchesOriginador = !appliedFilters.originador || linha.originador === appliedFilters.originador;
      const matchesCedente = !appliedFilters.cedente || linha.cedente === appliedFilters.cedente;
      const matchesStatus = !appliedFilters.status || linha.status === appliedFilters.status;

      return matchesFundo && matchesAprovador && matchesOriginador && matchesCedente && matchesStatus;
    });
  }, [transformedData, appliedFilters]);

  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters(prev => {
      if (!relacionamentos) return { ...prev, [key]: value };
      if (key === 'fundo') {
        const validCedentes = value ? new Set(relacionamentos.fundoCedentes.filter(fc => fc.nomeFundo === value).map(fc => fc.nomeCedente)) : null;
        const validOriginadores = value ? new Set(relacionamentos.fundoOriginadores.filter(fo => fo.nomeFundo === value).map(fo => fo.nomeOriginador)) : null;
        return {
          ...prev,
          fundo: value,
          cedente: validCedentes && prev.cedente && !validCedentes.has(prev.cedente) ? '' : prev.cedente,
          originador: validOriginadores && prev.originador && !validOriginadores.has(prev.originador) ? '' : prev.originador,
        };
      }
      if (key === 'cedente') {
        const validFundos = value ? new Set(relacionamentos.fundoCedentes.filter(fc => fc.nomeCedente === value).map(fc => fc.nomeFundo)) : null;
        const validOriginadores = value ? new Set(relacionamentos.originadorCedentes.filter(oc => oc.nomeCedente === value).map(oc => oc.nomeOriginador)) : null;
        return {
          ...prev,
          cedente: value,
          fundo: validFundos && prev.fundo && !validFundos.has(prev.fundo) ? '' : prev.fundo,
          originador: validOriginadores && prev.originador && !validOriginadores.has(prev.originador) ? '' : prev.originador,
        };
      }
      if (key === 'originador') {
        const validFundos = value ? new Set(relacionamentos.fundoOriginadores.filter(fo => fo.nomeOriginador === value).map(fo => fo.nomeFundo)) : null;
        const validCedentes = value ? new Set(relacionamentos.originadorCedentes.filter(oc => oc.nomeOriginador === value).map(oc => oc.nomeCedente)) : null;
        return {
          ...prev,
          originador: value,
          fundo: validFundos && prev.fundo && !validFundos.has(prev.fundo) ? '' : prev.fundo,
          cedente: validCedentes && prev.cedente && !validCedentes.has(prev.cedente) ? '' : prev.cedente,
        };
      }
      return { ...prev, [key]: value };
    });
  };

  const sortedData = useMemo((): CessaoRow[] => {
    if (!sortField) return filteredLinhaCessoes;
    return [...filteredLinhaCessoes].sort((a, b) => {
      const aVal = a[sortField as keyof CessaoRow];
      const bVal = b[sortField as keyof CessaoRow];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (typeof aVal === 'string' && typeof bVal === 'string')
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      if (typeof aVal === 'number' && typeof bVal === 'number')
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      return 0;
    });
  }, [filteredLinhaCessoes, sortField, sortDirection]);

  const totalElements = sortedData.length;
  const totalPages = Math.ceil(totalElements / pageSize);

  const paginatedData = useMemo(
    () => sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [sortedData, currentPage, pageSize]
  );

  const columns = useMemo((): ColumnDef<CessaoRow>[] => [
    { key: 'nome_remessa', label: 'Nome Remessa', sortable: true },
    { key: 'fundo', label: 'Fundo', sortable: true },
    { key: 'cedente', label: 'Cedente', sortable: true },
    { key: 'originador', label: 'Originador', sortable: true },
    { key: 'data_cessao', label: 'Data Cessão', sortable: true },
    {
      key: 'valor_aquisicao',
      label: 'Valor Aquisição',
      sortable: true,
      render: item => item.valor_aquisicao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    {
      key: 'valor_nominal',
      label: 'Valor Nominal',
      sortable: true,
      render: item => item.valor_nominal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
    },
    { key: 'quantidade_titulos', label: 'Qtd. Títulos', sortable: true },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: item => (
        <span className={`status-pill ${getStatusClass(item.id_status)}`}>
          {item.status}
        </span>
      ),
    },
  ], []);

  const handleClearFilters = () => {
    const hoje = getTodayDate();
    const emptyFilters = getEmptyFilters();

    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);

    setCurrentPage(0);
    setPendingDate(hoje);
    setDataCessaoFiltro(hoje);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setAppliedFilters(filters);
    setDataCessaoFiltro(pendingDate);
    if (pendingDate === dataCessaoFiltro) {
      fetchData(pendingDate);
    }
  };



  return (
    <>
      <PermissionGate required="VIEW_OPERATIONS">
        <TitleContainer>
          CESSÕES - {formattedDateTime}
        </TitleContainer>

        <FiltersCard
          actions={
            <>
              <Button onClick={handleClearFilters} variant="secondary" icon={faRefresh}>Limpar</Button>
              <Button onClick={handleSearch} variant="primary" icon={faSearch}>Buscar</Button>
            </>
          }
        >

          <DatePicker
            label="Data de Aquisição"
            value={pendingDate}
            onChange={(value) => setPendingDate(value)}
            placeholder="Selecione a data"
            maxDate={endOfCurrentMonth}
          />

          <Select
            label="Fundo"
            value={filters.fundo}
            onChange={v => handleFilterChange('fundo', v)}
            options={fundoOptions.map(fundo => ({ value: fundo, label: fundo }))}
            placeholder="Todos os fundos"
          />
          <Select
            label="Aprovador"
            value={filters.aprovador}
            onChange={v => handleFilterChange('aprovador', v)}
            options={uniqueAprovadores.map(aprovador => ({ value: aprovador, label: aprovador }))}
            placeholder="Todos os aprovadores"
          />

          <Select
            label="Originador"
            value={filters.originador}
            onChange={v => handleFilterChange('originador', v)}
            options={originadorOptions.map(originador => ({ value: originador, label: originador }))}
            placeholder="Todos os originadores"
          />
          <Select
            label="Cedente"
            value={filters.cedente}
            onChange={v => handleFilterChange('cedente', v)}
            options={cedenteOptions.map(cedente => ({ value: cedente, label: cedente }))}
            placeholder="Todos os cedentes"
          />
          <Select
            label="Status"
            value={filters.status}
            onChange={v => handleFilterChange('status', v)}
            options={uniqueStatus.map(status => ({ value: status, label: status }))}
            placeholder="Todos os status"
          />
        </FiltersCard>

          <DataTable<CessaoRow>
            data={paginatedData}
            columns={columns}
            loading={loading}
            loadingText="Carregando dados..."
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            rowKey={item => item.id_remessa}
            rowClassName={item => item.disabled ? 'disabled-row' : ''}
            emptyTitle="Nenhuma cessão encontrada"
            emptyMessage="Nenhuma cessão encontrada para a data selecionada."
            renderActions={item => {
              if (item.disabled) return null;
              return (
                <PermissionGate required="VIEW_BATCH_DETAILS">
                  <button
                    className="sap-button-link"
                    onClick={() => navigate(`/aquisicao/operacoes/detalhes/${item.id_remessa}`, { state: { id_status: item.id_status, id_produto: item.id_produto } })}
                  >
                    <FontAwesomeIcon icon={faEye} className="fa-eye" />
                  </button>
                </PermissionGate>
              );
            }}
            actionsLabel=""
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalElements={totalElements}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(0); }}
          />
      </PermissionGate>
    </>
  );
};

export default CredCockpit;